import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, EyeOff, Loader2, AlertTriangle, Calendar, MessageSquare, Clock } from "lucide-react";
import { getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { usePracticienKey } from "../lib/useCrypto";
import { decryptFernet } from "../lib/fernetUtils";
import { useStaff } from "../lib/StaffContext";
import {
  buildDigestActiveMetrics,
  buildDigestCategoryState,
  createDigestPriorityOpenAction,
} from "../lib/reopeningDigestOpen";
import dayjs from "dayjs";

const API_URL = import.meta.env.VITE_API_URL as string;
type DigestCategoryKey = "conversations" | "urgent" | "booking" | "admin";

interface DigestMetrics {
  touched_conversations?: number;
  source_messages?: number;
  urgent_count?: number;
  booking_count?: number;
  admin_count?: number;
}

interface DigestActiveMetrics {
  conversations?: number;
  urgent?: number;
  booking?: number;
  admin?: number;
}

interface DigestItem {
  id: string;
  utilisateur: string;
  priority_rank: number | null;
  priority_score: number | null;
  reason: string;
  preview?: string;
  is_priority?: boolean;
  signals_json: Record<string, boolean>;
  category?: string;
}

interface DigestCategories {
  conversations?: DigestItem[];
  urgent?: DigestItem[];
  booking?: DigestItem[];
  admin?: DigestItem[];
}

interface DigestDetail {
  id: string;
  status: "building" | "ready" | "empty" | "failed";
  window_start: string;
  window_end: string;
  executive_summary: string;
  metrics_json: DigestMetrics;
  active_metrics?: DigestActiveMetrics | null;
  llm_model: string | null;
  source_message_count: number;
  created_by_scope: string;
  created_by_label: string | null;
  created_at: string;
  priorities: DigestItem[];
  categories?: DigestCategories;
  hidden_for_viewer: boolean;
}

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  urgent:    { bg: "bg-red-100",    text: "text-red-700",    label: "Urgent" },
  booking:   { bg: "bg-amber-100",  text: "text-amber-700",  label: "Booking" },
  admin:     { bg: "bg-blue-100",   text: "text-blue-700",   label: "Admin" },
  follow_up: { bg: "bg-slate-100",  text: "text-slate-600",  label: "Follow-up" },
};
const DIGEST_CATEGORY_TITLES: Record<DigestCategoryKey, string> = {
  conversations: "All active conversations",
  urgent: "Urgent conversations",
  booking: "Booking conversations",
  admin: "Admin conversations",
};
const DIGEST_CATEGORY_EMPTY_MESSAGES: Record<DigestCategoryKey, string> = {
  conversations: "No active conversations",
  urgent: "No active urgent conversations",
  booking: "No active booking conversations",
  admin: "No active admin conversations",
};

function CategoryBadge({ category }: { category?: string }) {
  const style = CATEGORY_STYLES[category ?? "follow_up"] ?? CATEGORY_STYLES.follow_up;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

interface ReopeningDigestViewProps {
  digestId: string;
  praticienId: string;
  onHidden?: () => void;
  onOpenConversation?: (utilisateur: string) => void;
}

/**
 * Digest detail pane — rendered inside ConversationView / ConversationViewDesktop
 * when the route ID starts with "digest:".
 *
 * No fake chat bubbles. Reuses pane shell, provides digest-specific body.
 */
export default function ReopeningDigestView({
  digestId,
  praticienId,
  onHidden,
  onOpenConversation,
}: ReopeningDigestViewProps) {
  const user = useUser();
  const { key: practKey } = usePracticienKey();
  const { activeStaff } = useStaff();

  const [digest, setDigest] = useState<DigestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiding, setHiding] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<DigestCategoryKey | null>(null);
  const [doneSubmitting, setDoneSubmitting] = useState<Record<string, boolean>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  // Decrypted content (backend already decrypts server-side via Fernet)
  // But we also support client-side decryption fallback for summary/reasons
  // returned as ciphertext. In practice the backend decrypts, so these pass through.
  const [decryptedSummary, setDecryptedSummary] = useState<string>("");
  const [decryptedReasons, setDecryptedReasons] = useState<Record<string, string>>({});

  const staffId = activeStaff?.staff_id ?? null;

  const fetchDigest = useCallback(async ({ showLoader = true }: { showLoader?: boolean } = {}) => {
    if (showLoader) setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const params = new URLSearchParams({ praticien_id: praticienId });
      if (staffId) params.set("staff_id", staffId);

      const res = await fetch(
        `${API_URL}/internal/reopening-digests/${digestId}?${params}`,
        token ? { headers: { Authorization: token } } : {}
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail ?? `Error ${res.status}`);
        return;
      }

      const data: DigestDetail = await res.json();
      setDigest(data);
      // Do not drive hidden from hidden_for_viewer — the user opened this view
      // intentionally, so the hide button should always be available.

      // Attempt client-side decryption if summary looks like ciphertext
      // (backend normally decrypts, this is a belt-and-suspenders fallback)
      if (data.executive_summary && practKey) {
        try {
          const plain = await decryptFernet(practKey, data.executive_summary);
          setDecryptedSummary(plain);
        } catch {
          setDecryptedSummary(data.executive_summary);
        }
      } else {
        setDecryptedSummary(data.executive_summary ?? "");
      }

      // Decrypt reasons
      if (practKey && data.priorities?.length) {
        const map: Record<string, string> = {};
        for (const item of data.priorities) {
          if (!item.reason) continue;
          try {
            map[item.id] = await decryptFernet(practKey, item.reason);
          } catch {
            map[item.id] = item.reason;
          }
        }
        setDecryptedReasons(map);
      } else {
        setDecryptedReasons({});
      }
    } catch (e) {
      setError("Failed to load digest");
      console.error("[DIGEST VIEW]", e);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [digestId, praticienId, staffId, practKey]);

  useEffect(() => {
    fetchDigest();
  }, [fetchDigest]);

  // Poll while building
  useEffect(() => {
    if (!digest || digest.status !== "building") return;
    const timer = setInterval(() => {
      fetchDigest({ showLoader: false });
    }, 3000);
    return () => clearInterval(timer);
  }, [digest, fetchDigest]);

  const handleHide = async () => {
    setHiding(true);
    try {
      const token = await getAuthToken();
      const body: Record<string, string> = { praticien_id: praticienId };
      if (staffId) body.staff_id = staffId;

      await fetch(`${API_URL}/internal/reopening-digests/${digestId}/hide`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify(body),
      });

      setHidden(true);
      onHidden?.();
      window.dispatchEvent(new Event("digest-hidden"));
    } catch (e) {
      console.error("[DIGEST HIDE]", e);
    } finally {
      setHiding(false);
    }
  };

  const handleDone = useCallback(async (utilisateur: string) => {
    if (!utilisateur) return;
    setActionError(null);
    setDoneSubmitting((prev) => ({ ...prev, [utilisateur]: true }));
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/internal/reopening-digests/${digestId}/threads/done`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
        body: JSON.stringify({
          praticien_id: praticienId,
          staff_id: staffId,
          utilisateur,
        }),
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail ?? `Error ${res.status}`);
      }

      await fetchDigest({ showLoader: false });
      window.dispatchEvent(new Event("digest-updated"));
    } catch (e) {
      console.error("[DIGEST DONE]", e);
      setActionError("Failed to mark this conversation done. Please retry.");
    } finally {
      setDoneSubmitting((prev) => ({ ...prev, [utilisateur]: false }));
    }
  }, [digestId, fetchDigest, praticienId, staffId]);

  const windowLabel = (() => {
    if (!digest) return "";
    const start = dayjs(digest.window_start);
    const end = dayjs(digest.window_end);
    return `${start.format("DD MMM YYYY HH:mm")} – ${end.format("DD MMM YYYY HH:mm")}`;
  })();

  const generatedByLabel = (() => {
    if (!digest) return "";
    if (digest.created_by_label) return digest.created_by_label;
    const scope = digest.created_by_scope ?? "";
    if (scope.startsWith("user:")) return "Practitioner";
    if (scope.startsWith("staff:")) return "Staff member";
    return scope;
  })();

  // ── Render states ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="text-sm">Loading digest…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 text-slate-500 px-6">
        <AlertTriangle className="w-8 h-8 text-amber-400" />
        <p className="text-sm text-center">{error}</p>
        <button
          type="button"
          onClick={() => fetchDigest()}
          className="text-xs px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!digest) return null;

  if (digest.status === "building") {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 text-slate-400 px-6">
        <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        <p className="text-sm font-medium text-slate-600">Building your digest…</p>
        <p className="text-xs text-slate-400 text-center">
          Analysing conversations from the closed period. This takes a few seconds.
        </p>
      </div>
    );
  }

  if (digest.status === "failed") {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-3 px-6 text-slate-500">
        <AlertTriangle className="w-8 h-8 text-red-400" />
        <p className="text-sm font-medium text-slate-700">Digest generation failed</p>
        <p className="text-xs text-center text-slate-400">
          The digest could not be generated. Please try again later.
        </p>
      </div>
    );
  }

  if (digest.status === "empty") {
    return (
      <div className="flex flex-col flex-1">
        {/* Header */}
        <DigestHeader
          windowLabel={windowLabel}
          generatedAt={digest.created_at}
          generatedBy={generatedByLabel}
          onHide={handleHide}
          hiding={hiding}
          hidden={hidden}
        />
        <div className="flex flex-col flex-1 items-center justify-center gap-3 text-slate-400 px-6">
          <FileText className="w-8 h-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">No conversations during closure</p>
          <p className="text-xs text-center text-slate-400">
            No patient messages were received during the analyzed closure window.
          </p>
        </div>
      </div>
    );
  }

  // status === "ready"
  const snapshotMetrics = digest.metrics_json ?? {};
  const activeMetrics =
    digest.active_metrics ??
    (digest.categories?.conversations
      ? buildDigestActiveMetrics(digest.categories.conversations, new Set())
      : {
          conversations: snapshotMetrics.touched_conversations ?? 0,
          urgent: snapshotMetrics.urgent_count ?? 0,
          booking: snapshotMetrics.booking_count ?? 0,
          admin: snapshotMetrics.admin_count ?? 0,
        });
  const canExpandCategories = Boolean(digest.categories);
  const expandedRows = expandedCategory
    ? digest.categories?.[expandedCategory] ?? []
    : [];

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Header */}
      <DigestHeader
        windowLabel={windowLabel}
        generatedAt={digest.created_at}
        generatedBy={generatedByLabel}
        onHide={handleHide}
        hiding={hiding}
        hidden={hidden}
      />

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Executive summary */}
        {decryptedSummary && (
          <section>
            <p className="text-sm text-slate-700 leading-relaxed">{decryptedSummary}</p>
          </section>
        )}

        {/* Metrics strip */}
        <MetricsStrip
          metrics={activeMetrics}
          expandedCategory={expandedCategory}
          canExpand={canExpandCategories}
          onToggleCategory={(category) =>
            setExpandedCategory((current) => buildDigestCategoryState(current, category))
          }
        />

        {actionError && (
          <p className="text-xs text-red-500">{actionError}</p>
        )}

        {expandedCategory && (
          <DigestCategorySection
            category={expandedCategory}
            rows={expandedRows}
            doneSubmitting={doneSubmitting}
            onDone={handleDone}
            onOpenConversation={onOpenConversation}
          />
        )}

        {/* Prioritized conversations */}
        <section>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Priority conversations
          </h3>
          {digest.priorities.length > 0 ? (
            <ol className="space-y-2">
              {digest.priorities.map((item) => (
                <PriorityItem
                  key={item.id}
                  item={item}
                  reason={decryptedReasons[item.id] ?? item.reason ?? item.preview ?? ""}
                  doneSubmitting={!!doneSubmitting[item.utilisateur]}
                  onDone={handleDone}
                  onOpenConversation={onOpenConversation}
                />
              ))}
            </ol>
          ) : (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
              All priority conversations are done.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function DigestHeader({
  windowLabel,
  generatedAt,
  generatedBy,
  onHide,
  hiding,
  hidden,
}: {
  windowLabel: string;
  generatedAt: string;
  generatedBy: string;
  onHide: () => void;
  hiding: boolean;
  hidden: boolean;
}) {
  return (
    <div className="border-b border-slate-100 px-4 py-3 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center shrink-0 mt-0.5">
        <FileText className="w-4 h-4 text-teal-600" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">Morning digest</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
          {windowLabel && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <Calendar className="w-3 h-3" />
              {windowLabel}
            </span>
          )}
          {generatedAt && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <Clock className="w-3 h-3" />
              Generated {dayjs(generatedAt).format("HH:mm")}
              {generatedBy ? ` by ${generatedBy}` : ""}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        disabled={hiding}
        onClick={onHide}
        title="Hide digest"
        className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition disabled:opacity-50"
      >
        {hiding ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <EyeOff className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}

function MetricsStrip({
  metrics,
  expandedCategory,
  canExpand,
  onToggleCategory,
}: {
  metrics: DigestActiveMetrics;
  expandedCategory: DigestCategoryKey | null;
  canExpand: boolean;
  onToggleCategory?: (category: DigestCategoryKey) => void;
}) {
  const tiles = [
    {
      key: "conversations" as DigestCategoryKey,
      icon: <MessageSquare className="w-3.5 h-3.5" />,
      label: "Conversations",
      value: metrics.conversations ?? 0,
      color: "text-teal-600",
      bg: "bg-teal-50",
    },
    {
      key: "urgent" as DigestCategoryKey,
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      label: "Urgent",
      value: metrics.urgent ?? 0,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      key: "booking" as DigestCategoryKey,
      icon: <Calendar className="w-3.5 h-3.5" />,
      label: "Booking",
      value: metrics.booking ?? 0,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      key: "admin" as DigestCategoryKey,
      icon: <FileText className="w-3.5 h-3.5" />,
      label: "Admin",
      value: metrics.admin ?? 0,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
  ];

  return (
    <div className="grid grid-cols-4 gap-2">
      {tiles.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onToggleCategory?.(t.key)}
          disabled={!canExpand}
          aria-pressed={expandedCategory === t.key}
          className={`rounded-lg ${t.bg} px-2.5 py-2 flex flex-col items-center gap-0.5 border transition
            ${expandedCategory === t.key ? "border-slate-300 ring-1 ring-slate-200" : "border-transparent"}
            ${canExpand ? "hover:-translate-y-0.5 hover:shadow-sm" : "cursor-default"}
          `}
        >
          <span className={`${t.color} opacity-80`}>{t.icon}</span>
          <span className={`text-base font-bold ${t.color}`}>{t.value}</span>
          <span className="text-[10px] text-slate-500 text-center leading-tight">{t.label}</span>
        </button>
      ))}
    </div>
  );
}

function DigestCategorySection({
  category,
  rows,
  doneSubmitting,
  onDone,
  onOpenConversation,
}: {
  category: DigestCategoryKey;
  rows: DigestItem[];
  doneSubmitting: Record<string, boolean>;
  onDone: (utilisateur: string) => Promise<void>;
  onOpenConversation?: (utilisateur: string) => void;
}) {
  return (
    <section>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {DIGEST_CATEGORY_TITLES[category]}
        </h3>
        <span className="text-[11px] font-medium text-slate-400">{rows.length}</span>
      </div>
      {rows.length > 0 ? (
        <ol className="space-y-2">
          {rows.map((item) => (
            <DigestConversationItem
              key={item.id}
              item={item}
              description={item.preview || item.reason || "No recent messages"}
              doneSubmitting={!!doneSubmitting[item.utilisateur]}
              onDone={onDone}
              onOpenConversation={onOpenConversation}
              variant="category"
            />
          ))}
        </ol>
      ) : (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
          {DIGEST_CATEGORY_EMPTY_MESSAGES[category]}
        </p>
      )}
    </section>
  );
}

function PriorityItem({
  item,
  reason,
  doneSubmitting,
  onDone,
  onOpenConversation,
}: {
  item: DigestItem;
  reason: string;
  doneSubmitting: boolean;
  onDone: (utilisateur: string) => Promise<void>;
  onOpenConversation?: (utilisateur: string) => void;
}) {
  return (
    <DigestConversationItem
      item={item}
      description={reason}
      doneSubmitting={doneSubmitting}
      onDone={onDone}
      onOpenConversation={onOpenConversation}
      variant="priority"
    />
  );
}

function DigestConversationItem({
  item,
  description,
  doneSubmitting,
  onDone,
  onOpenConversation,
  variant,
}: {
  item: DigestItem;
  description: string;
  doneSubmitting: boolean;
  onDone: (utilisateur: string) => Promise<void>;
  onOpenConversation?: (utilisateur: string) => void;
  variant: "priority" | "category";
}) {
  const displayPhone = item.utilisateur.replace(/^whatsapp:/, "");
  const openAction = createDigestPriorityOpenAction(
    item.utilisateur,
    onOpenConversation
  );

  return (
    <li className="flex items-start gap-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
      {variant === "priority" && item.priority_rank !== null && (
        <span className="shrink-0 w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center mt-0.5">
          {item.priority_rank}
        </span>
      )}

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-800 truncate">{displayPhone}</span>
          <CategoryBadge category={item.category ?? inferCategory(item)} />
          {variant === "category" && item.is_priority && item.priority_rank !== null && (
            <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
              Priority #{item.priority_rank}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs text-slate-500 leading-snug">{description}</p>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-2 mt-0.5">
        <Link
          to={openAction.href}
          onClick={(event) => openAction.onClick(event)}
          className="text-[11px] font-medium text-teal-600 hover:text-teal-800 hover:underline whitespace-nowrap"
        >
          Open
        </Link>
        <button
          type="button"
          disabled={doneSubmitting}
          onClick={() => onDone(item.utilisateur)}
          className="inline-flex min-w-[56px] items-center justify-center rounded-md bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-slate-700 disabled:cursor-wait disabled:bg-slate-400"
        >
          {doneSubmitting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Done"}
        </button>
      </div>
    </li>
  );
}

function inferCategory(item: DigestItem): string {
  const sig = item.signals_json ?? {};
  if (sig.contains_pain_terms) return "urgent";
  if (sig.contains_booking_terms || sig.contains_cancellation_terms) return "booking";
  if (sig.contains_admin_terms) return "admin";
  return "follow_up";
}
